import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOJsonLD from '@/components/base/SEOJsonLD';
import { aboutData } from '@/mocks/about';
import { useState, type FormEvent } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qsu6nnahb7382ovf90', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* noop */ }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "关于红桃tv - 高清视频娱乐平台",
    "description": "红桃tv成立于2018年，是国内领先的高清视频娱乐平台。了解红桃tv的品牌故事、核心价值观、发展历程和联系方式。",
    "url": "https://hongtaotv.com/about",
    "isPartOf": {
      "@type": "WebSite",
      "name": "红桃tv",
      "url": "https://hongtaotv.com/"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://hongtaotv.com/" },
      { "@type": "ListItem", "position": 2, "name": "关于我们" }
    ]
  };

  return (
    <>
      <SEOJsonLD data={aboutSchema} />
      <SEOJsonLD data={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main>
        {/* Brand Intro */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-10">
            <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 bg-primary-50 rounded-2xl flex items-center justify-center">
              <i className="ri-heart-fill text-primary-500 text-2xl md:text-3xl"></i>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-900 mb-4">
              关于红桃tv
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-3xl mx-auto leading-relaxed">
              {aboutData.brandIntro}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-14">
            {aboutData.stats.map((stat) => (
              <div key={stat.label} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 text-center">
                <div className="w-10 h-10 mx-auto mb-3 bg-accent-50 rounded-xl flex items-center justify-center">
                  <i className={`${stat.icon} text-accent-500 text-lg`}></i>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                  {stat.value}
                </p>
                <p className="text-xs md:text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Mission & Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-14">
            <div className="bg-primary-50/50 border border-primary-200/50 rounded-lg p-6 md:p-8">
              <div className="w-10 h-10 mb-3 bg-primary-100 rounded-lg flex items-center justify-center">
                <i className="ri-focus-3-line text-primary-500 text-lg"></i>
              </div>
              <h2 className="font-heading text-lg md:text-xl text-foreground-900 mb-2">我们的使命</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{aboutData.mission}</p>
            </div>
            <div className="bg-accent-50/50 border border-accent-200/50 rounded-lg p-6 md:p-8">
              <div className="w-10 h-10 mb-3 bg-accent-100 rounded-lg flex items-center justify-center">
                <i className="ri-eye-line text-accent-500 text-lg"></i>
              </div>
              <h2 className="font-heading text-lg md:text-xl text-foreground-900 mb-2">我们的愿景</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{aboutData.vision}</p>
            </div>
          </div>

          {/* Values */}
          <div className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">核心价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutData.values.map((v) => (
                <div key={v.title} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 bg-secondary-50 rounded-lg flex items-center justify-center">
                    <i className="ri-check-double-line text-secondary-500 text-lg"></i>
                  </div>
                  <h3 className="font-heading text-base md:text-lg text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section id="milestones" className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-10">发展历程</h2>
            <div className="max-w-3xl mx-auto relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2"></div>
              <div className="flex flex-col gap-6">
                {aboutData.milestones.map((m, idx) => {
                  const isOdd = idx % 2 === 0;
                  return (
                    <div key={m.year} className={`flex items-start gap-4 md:gap-8 ${isOdd ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="hidden md:flex flex-1"></div>
                      <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 border-4 border-background-50 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-background-50"></div>
                      </div>
                      <div className="flex-1 bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
                        <span className="inline-block text-xs text-primary-500 bg-primary-50 px-2 py-0.5 rounded mb-2">{m.year}</span>
                        <h3 className="font-heading text-base text-foreground-900 mb-1">{m.title}</h3>
                        <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{m.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">用户口碑</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {aboutData.testimonials.map((t) => (
              <div key={t.name} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                <div className="flex items-center gap-1 mt-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="flex flex-col gap-4">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-500">{aboutData.contactInfo.address}</p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">客服电话</h3>
                    <p className="text-sm text-foreground-500">{aboutData.contactInfo.phone}</p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">{aboutData.contactInfo.email}</p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-500">{aboutData.contactInfo.workingHours}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h3 className="font-heading text-lg text-foreground-900 mb-4">在线留言</h3>
                <form className="flex flex-col gap-3" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="name"
                    placeholder="您的姓名"
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="您的邮箱"
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    required
                  />
                  <textarea
                    name="message"
                    placeholder="请输入您的留言..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    required
                  ></textarea>
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden={true} readOnly />
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 bg-green-50 px-4 py-2 rounded-md">留言已发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-md">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}